import type { Pad } from "../shared/types";

const RADIUS = 52;
const RUN = 0.3;

/** Keyboard plus the on-screen stick. One-shot edges clear after the sim consumes them. */
export class Controls {
  aimX = 0;
  aimY = 0;
  aiming = false;
  hasPointer = false;
  clientX = 0;
  clientY = 0;
  visual = { on: false, x: 0, y: 0, kx: 0, ky: 0 };

  private leftK = false;
  private rightK = false;
  private jumpK = false;
  private downK = false;
  private leftS = false;
  private rightS = false;
  private jumpS = false;
  private downS = false;
  private jumpEdge = false;
  private downEdge = false;
  private fireHeld = false;
  private fireEdge = false;
  private altHeld = false;
  private altEdge = false;
  private slotN = -1;
  private cycleN = 0;
  private stickId = -1;
  private stickX = 0;
  private stickY = 0;
  private stickJump = false;
  private stickDrop = false;
  private aimId = -1;

  read(): Pad {
    return {
      left: this.leftK || this.leftS,
      right: this.rightK || this.rightS,
      jump: this.jumpK || this.jumpS,
      jumpPressed: this.jumpEdge,
      down: this.downK || this.downS,
      downPressed: this.downEdge,
      fire: this.fireHeld,
      firePressed: this.fireEdge,
      alt: this.altHeld,
      altPressed: this.altEdge,
      aimX: this.aimX,
      aimY: this.aimY,
      slot: this.slotN,
      cycle: this.cycleN,
    };
  }

  consume() {
    this.jumpEdge = false;
    this.downEdge = false;
    this.fireEdge = false;
    this.altEdge = false;
    this.slotN = -1;
    this.cycleN = 0;
  }

  blur() {
    this.leftK = this.rightK = this.jumpK = this.downK = false;
    this.fireHeld = false;
    this.altHeld = false;
  }

  key(e: KeyboardEvent, down: boolean) {
    if (e.repeat) return;
    const code = e.code;
    if (code === "KeyA" || code === "ArrowLeft") this.leftK = down;
    else if (code === "KeyD" || code === "ArrowRight") this.rightK = down;
    else if (code === "KeyW" || code === "ArrowUp" || code === "Space") {
      if (down && !this.jumpK) this.jumpEdge = true;
      this.jumpK = down;
    } else if (code === "KeyS" || code === "ArrowDown") {
      if (down && !this.downK) this.downEdge = true;
      this.downK = down;
    } else if (down && code === "KeyQ") this.cycleN -= 1;
    else if (down && code === "KeyE") this.cycleN += 1;
    else if (code === "KeyG" && !e.ctrlKey && !e.metaKey && !e.altKey) {
      if (down) this.altDown();
      else this.altUp();
    } else if (down && code.startsWith("Digit")) {
      const n = Number(code.slice(5));
      if (n >= 0 && n <= 9) this.slotN = n === 0 ? 100 : n - 1;
    }
  }

  wheel(deltaY: number) {
    this.cycleN += deltaY > 0 ? 1 : -1;
  }

  slot(index: number) {
    this.slotN = index;
  }

  altDown() {
    if (!this.altHeld) this.altEdge = true;
    this.altHeld = true;
  }

  altUp() {
    this.altHeld = false;
  }

  pointerDown(e: PointerEvent, local: { x: number; y: number }, width: number, height: number) {
    if (e.pointerType === "mouse") {
      this.note(e);
      if (e.button === 2) this.altDown();
      else if (e.button === 0) {
        if (!this.fireHeld) this.fireEdge = true;
        this.fireHeld = true;
      }
      return;
    }
    const top = height > width ? 0.45 : 0.35;
    if (this.stickId < 0 && local.x < width * 0.45 && local.y > height * top) {
      this.stickId = e.pointerId;
      this.stickX = local.x;
      this.stickY = local.y;
      this.visual = { on: true, x: local.x, y: local.y, kx: 0, ky: 0 };
      this.applyStick(0, 0);
      return;
    }
    if (this.aimId < 0) {
      this.aimId = e.pointerId;
      this.aiming = true;
      this.note(e);
      if (!this.fireHeld) this.fireEdge = true;
      this.fireHeld = true;
    }
  }

  pointerMove(e: PointerEvent, local: { x: number; y: number }) {
    if (e.pointerType === "mouse") {
      this.note(e);
      return;
    }
    if (e.pointerId === this.stickId) {
      this.applyStick(local.x - this.stickX, local.y - this.stickY);
      return;
    }
    if (e.pointerId === this.aimId) this.note(e);
  }

  pointerUp(e: PointerEvent) {
    if (e.pointerType === "mouse") {
      if (e.button === 0) this.fireHeld = false;
      if (e.button === 2) this.altHeld = false;
      return;
    }
    if (e.pointerId === this.stickId) {
      this.stickId = -1;
      this.leftS = this.rightS = this.jumpS = this.downS = false;
      this.stickJump = false;
      this.stickDrop = false;
      this.visual.on = false;
    }
    if (e.pointerId === this.aimId) {
      this.aimId = -1;
      this.aiming = false;
      this.fireHeld = false;
    }
  }

  /** Remember a screen point so the crosshair survives a click that never hits the canvas. */
  point(x: number, y: number) {
    this.hasPointer = true;
    this.clientX = x;
    this.clientY = y;
  }

  private note(e: PointerEvent) {
    this.point(e.clientX, e.clientY);
  }

  private applyStick(dx: number, dy: number) {
    const mag = Math.hypot(dx, dy);
    if (mag > RADIUS) {
      dx = (dx / mag) * RADIUS;
      dy = (dy / mag) * RADIUS;
    }
    this.visual.kx = dx;
    this.visual.ky = dy;
    this.visual.on = true;
    const nx = dx / RADIUS;
    const ny = dy / RADIUS;
    this.leftS = nx < -RUN;
    this.rightS = nx > RUN;
    const wantJump = this.stickJump ? ny < -0.35 : ny < -0.5;
    if (wantJump && !this.stickJump) this.jumpEdge = true;
    this.stickJump = wantJump;
    this.jumpS = wantJump;
    const wantDrop = this.stickDrop ? ny > 0.4 : ny > 0.55;
    if (wantDrop && !this.stickDrop) this.downEdge = true;
    this.stickDrop = wantDrop;
    this.downS = wantDrop;
  }
}
